import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getSession, getUserProgress, getActiveTrackId, getFluencyPracticeMetrics, saveFluencyBestStreak } from '../../config/api';
import { Fact } from '../../types';
import { UserProgress } from '../../types/progress';
import QuestionScreen from '../FluencyPractice/QuestionScreen';
import { useSession } from '../../context/SessionContext';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import FireAnimation from './FireAnimation';
import { playIgniteSound, playPowerUpSound, playEpicFanfare, playCorrectSound, playStreakSound, playTimerWarningSound, startBackgroundMusic } from './sounds';
import { updatePracticeContext } from '../../utils/practiceContext';

// Fluency levels and their timer durations (same as FluencyPracticePage)
const FLUENCY_LEVELS: Record<string, number> = {
  '6s': 6,
  '3s': 3,
  '2s': 2,
  '1.5s': 1.5,
  '1s': 1
};

// Drill only uses 3s+ levels (exclude 6s)
const DRILL_LEVELS = ['3s', '2s', '1.5s', '1s'];

type FluencyLevel = keyof typeof FLUENCY_LEVELS;

// Map fluency level to factsByStage key
const mapFluencyLevelToStageKey = (level: string): string => {
  switch (level) {
    case '3s': return 'fluency3Practice';
    case '2s': return 'fluency2Practice';
    case '1.5s': return 'fluency1_5Practice';
    case '1s': return 'fluency1Practice';
    default: return 'fluency3Practice';
  }
};

// DrillData uses the fluencyPractice shape (groupedFacts with fillers)
interface DrillData {
  groupedFacts?: { [key: string]: Fact[] };
  facts?: Fact[];
  fluencyLevel?: string;
}

const FIRE_COLORS = ['#ff6b35', '#ff4500', '#ffa500', '#ff8c00', '#ffcc02'];

const FluencyDrillPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { recordPageTransition } = useSession();
  const introSoundPlayed = useRef(false);
  const streakRef = useRef(0);
  const sessionBestStreakRef = useRef(0);
  const stopMusicRef = useRef<(() => void) | null>(null);

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [bestStreak, setBestStreak] = useState<number | null>(null);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [sessionBestStreak, setSessionBestStreak] = useState(0);
  const [drillData, setDrillData] = useState<DrillData | null>(null);
  const [availableLevels, setAvailableLevels] = useState<string[]>([]);
  const [currentLevelIndex, setCurrentLevelIndex] = useState(0);
  const [timerDuration, setTimerDuration] = useState(3);
  const [facts, setFacts] = useState<Fact[]>([]);
  const [screen, setScreen] = useState<'intro' | 'question' | 'celebration'>('intro');
  const [isCompleted, setIsCompleted] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [trackProgress, setTrackProgress] = useState<UserProgress | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Initialize drill data from session API
  useEffect(() => {
    const initializeDrill = async () => {
      if (!user?.userId) return;

      try {
        setIsLoading(true);

        // Always fetch fresh data from session API to avoid stale location state
        const sessionResponse = await getSession(user.userId, 'all');

        // Check drill is still active
        if (!sessionResponse.availableActivities.fluencyDrill?.active) {
          navigate('/', { replace: true });
          return;
        }

        // Use fluencyPractice data (built by normal flow with fillers)
        let data: DrillData | null = sessionResponse.availableActivities.fluencyPractice || null;

        if (!data?.groupedFacts) {
          navigate('/', { replace: true });
          return;
        }

        setDrillData(data);

        // Fetch user progress and best streak in parallel
        const trackId = getActiveTrackId();
        const [progressResponse, metricsResponse] = await Promise.all([
          getUserProgress(user.userId),
          getFluencyPracticeMetrics(user.userId).catch(() => ({ bestStreak: null }))
        ]);
        const currentTrack = progressResponse.tracks.find(t => t.trackId === trackId) || null;
        setTrackProgress(currentTrack);
        setBestStreak(metricsResponse.bestStreak);

        // Determine available levels — filter to 3s+ only (exclude 6s), sorted slowest to fastest
        const levels = Object.keys(data.groupedFacts)
          .filter(level => DRILL_LEVELS.includes(level) && data!.groupedFacts![level]?.length > 0)
          .sort((a, b) => (FLUENCY_LEVELS[b] || 0) - (FLUENCY_LEVELS[a] || 0));

        if (levels.length === 0) {
          navigate('/', { replace: true });
          return;
        }

        setAvailableLevels(levels);

        // Set initial level
        const initialLevel = levels[0];
        const initialFacts = data.groupedFacts[initialLevel] || [];
        setFacts(initialFacts);
        setTimerDuration(FLUENCY_LEVELS[initialLevel] || 3);
      } catch (error) {
        console.error('Error initializing fluency drill:', error);
        navigate('/', { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    initializeDrill();
  }, [user?.userId, refreshKey]);

  // Play ignite sound + start background music on intro screen mount
  useEffect(() => {
    if (screen === 'intro' && !isLoading && !introSoundPlayed.current) {
      introSoundPlayed.current = true;
      playIgniteSound();
      // Start music on intro (works because user clicked to navigate here)
      if (!stopMusicRef.current) {
        stopMusicRef.current = startBackgroundMusic();
      }
    }
  }, [screen, isLoading]);

  // Stop background music on unmount
  useEffect(() => {
    return () => {
      stopMusicRef.current?.();
    };
  }, []);

  // Sound + confetti feedback on every answer
  const handleAnswerResult = useCallback((correct: boolean) => {
    if (correct) {
      streakRef.current += 1;
      setCurrentStreak(streakRef.current);
      const streak = streakRef.current;

      if (streak > sessionBestStreakRef.current) {
        sessionBestStreakRef.current = streak;
        setSessionBestStreak(streak);
      }

      if (streak >= 3 && streak % 2 === 1) {
        // Streak milestone: play escalating streak sound + confetti burst
        playStreakSound(streak);
        confetti({
          particleCount: 30 + streak * 5,
          spread: 50 + streak * 5,
          origin: { y: 0.7 },
          colors: FIRE_COLORS,
        });
      } else {
        playCorrectSound();
      }
    } else {
      streakRef.current = 0;
      setCurrentStreak(0);
    }
  }, []);

  // Timer warning sound
  const handleTimerWarning = useCallback(() => {
    playTimerWarningSound();
  }, []);

  // Handle starting the drill
  const handleStart = () => {
    if (availableLevels.length === 0) return;

    // Set practice context so progress records under fluencyN instead of 'default'
    updatePracticeContext('fluency');

    // Fire confetti burst on start
    confetti({ particleCount: 80, spread: 60, origin: { y: 0.6 }, colors: FIRE_COLORS });

    // Ensure music is playing (fallback if intro didn't trigger it)
    if (!stopMusicRef.current) {
      stopMusicRef.current = startBackgroundMusic();
    }

    const currentLevel = availableLevels[0];
    const levelFacts = drillData?.groupedFacts?.[currentLevel] || [];
    setFacts(levelFacts);
    setTimerDuration(FLUENCY_LEVELS[currentLevel] || 3);
    setCurrentLevelIndex(0);

    // Record page transition
    if (user?.userId && levelFacts.length > 0) {
      const stageKey = mapFluencyLevelToStageKey(currentLevel);
      recordPageTransition('fluency-practice', {
        [stageKey]: levelFacts.map(f => f.factId)
      });
    }

    setScreen('question');
  };

  // Handle level completion — poll backend to check actual drill status
  const handleLevelComplete = useCallback(async () => {
    // Immediately show transition screen so kid isn't stuck on the last question
    setIsPolling(true);
    confetti({ particleCount: 40, spread: 50, origin: { y: 0.6 }, colors: FIRE_COLORS });

    // Wait for progress queue to flush
    await new Promise(resolve => setTimeout(resolve, 2000));

    let drillCleared = false;

    try {
      if (user?.userId) {
        const sessionResponse = await getSession(user.userId, 'all');

        if (!sessionResponse.availableActivities.fluencyDrill?.active) {
          drillCleared = true;
        }
      }
    } catch (error) {
      console.error('Error polling session after level completion:', error);
      // On error, assume cleared and proceed with normal flow
      drillCleared = true;
    }

    setIsPolling(false);

    if (drillCleared) {
      // Save best streak (fire-and-forget)
      if (sessionBestStreakRef.current > 0 && user?.userId) {
        saveFluencyBestStreak(user.userId, sessionBestStreakRef.current).catch(() => {});
      }
      // All done — celebration
      stopMusicRef.current?.();
      stopMusicRef.current = null;
      setIsCompleted(true);
      setScreen('celebration');
      setTimeout(() => navigate('/', { replace: true }), 5000);
    } else {
      // Drill still active — reload page to re-fetch fresh data and show intro with updated counts
      setRefreshKey(k => k + 1);
      setScreen('intro');
    }
  }, [user?.userId, navigate]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-orange-50 to-amber-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading Challenge Mode...</p>
        </div>
      </div>
    );
  }

  // Intro screen
  if (screen === 'intro') {
    // Count incomplete fluency facts for a specific level — only facts whose progressStatus
    // matches the level's stage key, minus those already completed today
    const storedPracticeDate = localStorage.getItem(`dailyPractice_${getActiveTrackId()}_date`);

    const isFluencyFactCompleted = (factId: string): boolean => {
      if (!trackProgress?.facts) return false;
      const factProgress = trackProgress.facts[factId];
      if (!factProgress) return false;

      if (factProgress.status === 'mastered') return true;

      if (!factProgress.todayStats) return false;
      for (const context in factProgress.todayStats) {
        if (context.startsWith('fluency')) {
          const stats = factProgress.todayStats[context];
          if (stats.date === storedPracticeDate &&
              stats.correct >= 3 && stats.attempts === stats.correct) {
            return true;
          }
        }
      }
      return false;
    };

    const countIncompleteFluencyFacts = (facts: Fact[] | undefined, level: string) => {
      const stageKey = mapFluencyLevelToStageKey(level);
      return facts?.filter((f: any) =>
        f.progressStatus === stageKey && !isFluencyFactCompleted(f.factId)
      ).length || 0;
    };

    const incompleteLevels = availableLevels.filter(level =>
      countIncompleteFluencyFacts(drillData?.groupedFacts?.[level], level) > 0
    );
    const totalFacts = incompleteLevels.reduce((sum, level) => {
      return sum + countIncompleteFluencyFacts(drillData?.groupedFacts?.[level], level);
    }, 0);
    const levelCount = incompleteLevels.length;

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-orange-50 to-amber-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-md mx-auto p-8 text-center"
        >
          {/* Animated fire icon */}
          <div className="mx-auto mb-6 flex items-center justify-center">
            <FireAnimation size="lg" />
          </div>

          <h1 className="text-3xl font-bold text-gray-800 mb-3">
            Challenge Mode
          </h1>
          <p className="text-gray-600 mb-6">
            Time to level up! Complete {totalFacts} facts across {levelCount} speed {levelCount === 1 ? 'level' : 'levels'} to unlock your regular practice.
          </p>

          {/* Level preview */}
          <div className="bg-white rounded-xl p-4 mb-6 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Speed Levels</h3>
            <div className="space-y-2">
              {availableLevels.map((level, i) => {
                const factCount = countIncompleteFluencyFacts(drillData?.groupedFacts?.[level], level);
                if (factCount === 0) return null;
                return (
                  <div key={level} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">{level} timer</span>
                    </div>
                    <span className="text-sm text-gray-500">{factCount} facts</span>
                  </div>
                );
              })}
            </div>
          </div>

          <p className="text-sm text-gray-500 mb-6">
            Get 3 correct answers for each fact to complete the challenge.
          </p>

          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleStart}
            className="w-full py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold text-lg rounded-xl shadow-md hover:shadow-lg transition-shadow"
          >
            Start Challenge
          </motion.button>
        </motion.div>
      </div>
    );
  }

  // Celebration screen
  if (screen === 'celebration') {
    return <CelebrationScreen />;
  }

  // Polling backend after level completion — brief transition screen
  if (isPolling) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-orange-50 to-amber-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="flex justify-center mb-4">
            <FireAnimation size="md" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Nice work!</h2>
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mt-4"></div>
        </motion.div>
      </div>
    );
  }

  // Question screen — reuse existing QuestionScreen component
  // In drill mode, ALL facts are "practice" facts (need 3 correct each)
  const allFactPKs = facts.map(f => f.PK);

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-amber-50">
      {/* Challenge Mode header bar with animated gradient */}
      <div
        className="text-white px-4 py-2 flex items-center justify-between"
        style={{
          background: 'linear-gradient(270deg, #ff4500, #ff6b35, #ff8c00, #ff4500)',
          backgroundSize: '200% 200%',
          animation: 'gradientShift 3s ease infinite',
        }}
      >
        <div className="flex items-center gap-2">
          <FireAnimation size="sm" />
          <span className="font-bold text-sm">Challenge Mode</span>
        </div>
        <div className="text-sm">
          Level {currentLevelIndex + 1} of {availableLevels.length} &mdash; {availableLevels[currentLevelIndex]} speed
        </div>
      </div>
      <style>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>

      <QuestionScreen
        questions={facts}
        practiceFacts={allFactPKs}
        otherFacts={[]}
        onComplete={handleLevelComplete}
        timerDuration={timerDuration}
        onAnswerResult={handleAnswerResult}
        onTimerWarning={handleTimerWarning}
        hideKeypad
        immediateProgress
        currentStreak={currentStreak}
        sessionBestStreak={Math.max(bestStreak ?? 0, sessionBestStreak)}
      />
    </div>
  );
};

/** Level transition sub-screen with sound + confetti + fire animation. */
/** Celebration sub-screen with epic fanfare, triple confetti, and fire animation. */
const CelebrationScreen: React.FC = () => {
  useEffect(() => {
    playEpicFanfare();

    // Triple confetti bursts at 0ms, 300ms, 600ms
    confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 }, colors: FIRE_COLORS });
    const t1 = setTimeout(() => {
      confetti({ particleCount: 60, spread: 100, origin: { y: 0.5 }, colors: FIRE_COLORS });
    }, 300);
    const t2 = setTimeout(() => {
      confetti({ particleCount: 100, spread: 120, origin: { y: 0.4 }, colors: FIRE_COLORS });
    }, 600);

    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 via-orange-50 to-red-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: 'spring', damping: 12 }}
        className="text-center max-w-md mx-auto p-8 relative"
      >
        {/* Fire behind trophy */}
        <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-2 opacity-60 pointer-events-none">
          <FireAnimation size="lg" />
        </div>

        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: [0, 1.3, 1] }}
          transition={{ type: 'spring', damping: 8, stiffness: 150 }}
          className="text-7xl mb-4 relative z-10"
        >
          &#x1F3C6;
        </motion.div>
        <h2 className="text-3xl font-bold text-gray-800 mb-3 relative z-10">Challenge Complete!</h2>
        <p className="text-gray-600 mb-6 relative z-10">
          Great job! You conquered all the speed levels. Heading back to your dashboard...
        </p>
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto relative z-10"></div>
      </motion.div>
    </div>
  );
};

export default FluencyDrillPage;
